/* ==========================================================================
   COVE SODA v3 → Shopify port — shared section behavior (cove-v3.js)
   Vanilla only: rAF marquees (fwd + reverse), IO entrance reveals, film
   play/pause, stat count-up, shop carousel arrows, press click-rotator.
   No GSAP, no CDNs. Loaded with `defer` by every cv3 section; idempotent so
   duplicate tags are harmless. Reduced-motion safe.
   ========================================================================== */

(() => {
  'use strict';

  if (window.__coveV3) return;
  window.__coveV3 = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // ?noanim disables the motion layer for screenshot/QA tooling.
  const noAnim = new URLSearchParams(location.search).has('noanim');
  const hasIO = 'IntersectionObserver' in window;

  /* ------------------------------------------------------------------ Marquees (rAF, CDN-free)
     Drives the claims ticker, retailer band, and community rows. Each
     [data-marquee] holds one [data-marquee-set] cloned for a seamless loop;
     data-marquee-speed tunes px/frame; data-marquee-reverse flips direction.
     Pauses on hover/focus (WCAG 2.2.2). Reduced-motion: static row via CSS. */
  const initMarquees = (root) => {
    root.querySelectorAll('[data-marquee]').forEach((track) => {
      if (track.dataset.cv3MarqueeInit) return;
      track.dataset.cv3MarqueeInit = '1';
      const set = track.querySelector('[data-marquee-set]');
      if (!set || reduceMotion) return;
      track.classList.add('cv3-is-marquee');
      const copies = Math.max(2, Math.ceil((Math.max(window.innerWidth, 2560) * 2) / Math.max(set.offsetWidth, 200)) + 1);
      for (let i = 0; i < copies; i++) {
        const clone = set.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      }
      const speed = parseFloat(track.dataset.marqueeSpeed || '0.55');
      const reverse = track.hasAttribute('data-marquee-reverse');
      let x = reverse ? -set.offsetWidth : 0, paused = false;
      const zone = track.parentElement || track;
      zone.addEventListener('mouseenter', () => { paused = true; });
      zone.addEventListener('mouseleave', () => { paused = false; });
      zone.addEventListener('focusin', () => { paused = true; });
      zone.addEventListener('focusout', () => { paused = false; });
      const tick = () => {
        if (!track.isConnected) return; // stop dead loops after editor re-renders
        if (!paused) {
          const w = set.offsetWidth;
          x += reverse ? speed : -speed;
          if (!reverse && -x >= w) x += w;
          if (reverse && x >= 0) x -= w;
          track.style.transform = `translateX(${x}px)`;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  };

  /* ------------------------------------------------------------------ Shop carousel
     Prev/next arrows advance the flavor strip one card at a time; wraps at
     either end. Arrows live as siblings of [data-carousel] inside the wrapper. */
  const initCarousel = (root) => {
    root.querySelectorAll('[data-carousel]').forEach((track) => {
      if (track.dataset.cv3CarInit) return;
      track.dataset.cv3CarInit = '1';
      const scope = track.parentElement || document;
      const prev = scope.querySelector('[data-car-prev]');
      const next = scope.querySelector('[data-car-next]');
      if (!prev || !next) return;
      const step = () => {
        const card = track.querySelector('.cv3-card') || track.firstElementChild;
        if (!card) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).gap) || 16;
        return card.getBoundingClientRect().width + gap;
      };
      const go = (dir) => {
        const max = track.scrollWidth - track.clientWidth;
        const x = track.scrollLeft;
        let target = x + dir * step();
        if (dir > 0 && x >= max - 2) target = 0;
        else if (dir < 0 && x <= 2) target = max;
        track.scrollTo({ left: target, behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      prev.addEventListener('click', () => go(-1));
      next.addEventListener('click', () => go(1));
    });
  };

  /* ------------------------------------------------------------------ Press rotator
     Click a logo to reveal its quote. Panels are rendered server-side (Liquid);
     JS only toggles the active panel + pressed state. No auto-rotation. */
  const initPress = (root) => {
    root.querySelectorAll('[data-cv3-press]').forEach((wrap) => {
      if (wrap.dataset.cv3PressInit) return;
      wrap.dataset.cv3PressInit = '1';
      const list = wrap.querySelector('[data-cv3-press-logos]');
      const panels = [...wrap.querySelectorAll('[data-cv3-press-q]')];
      const logos = [...wrap.querySelectorAll('button[data-outlet]')];
      if (!list || !panels.length) return;
      const show = (outlet) => {
        panels.forEach((p) => p.classList.toggle('cv3-is-active', p.dataset.outlet === outlet));
        logos.forEach((b) => {
          const on = b.dataset.outlet === outlet;
          b.classList.toggle('cv3-is-active', on);
          b.setAttribute('aria-pressed', String(on));
        });
      };
      list.addEventListener('click', (e) => {
        const b = e.target.closest('button[data-outlet]');
        if (b && b.dataset.outlet) show(b.dataset.outlet);
      });
    });
  };

  /* ------------------------------------------------------------------ Film */
  const initFilm = (root) => {
    root.querySelectorAll('[data-film]').forEach((video) => {
      if (video.dataset.cv3FilmInit) return;
      video.dataset.cv3FilmInit = '1';
      const scope = video.closest('.cv3-film') || video.parentElement || document;
      const toggle = scope.querySelector('[data-film-toggle]');
      if (!toggle) return;
      let userPaused = reduceMotion;
      const iconPause = toggle.querySelector('[data-icon-pause]');
      const iconPlay = toggle.querySelector('[data-icon-play]');
      const setUI = (paused) => {
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', paused ? 'Play background video' : 'Pause background video');
        if (iconPause) iconPause.style.display = paused ? 'none' : '';
        if (iconPlay) iconPlay.style.display = paused ? '' : 'none';
      };
      setUI(userPaused);
      video.addEventListener('play', () => { if (!userPaused) setUI(false); });
      video.addEventListener('pause', () => { if (userPaused) setUI(true); });
      toggle.addEventListener('click', () => {
        userPaused = !userPaused;
        setUI(userPaused);
        if (userPaused) video.pause();
        else video.play().catch(() => {});
      });
      if (hasIO) {
        new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting && !userPaused) video.play().catch(() => {});
            else video.pause();
          });
        }, { threshold: 0.25 }).observe(video);
      } else if (!userPaused) {
        video.play().catch(() => {});
      }
    });
  };

  /* ------------------------------------------------------------------ Stat count-ups */
  const setFinalCount = (el) => {
    const target = parseInt(el.dataset.count, 10);
    if (!Number.isNaN(target)) el.textContent = target.toLocaleString() + (el.dataset.countSuffix || '');
  };
  const runCount = (el) => {
    const target = parseInt(el.dataset.count, 10);
    if (Number.isNaN(target)) return;
    const suffix = el.dataset.countSuffix || '';
    const duration = 2200;                        // matches the static site's 2.2s
    const easeOut = (t) => 1 - Math.pow(1 - t, 2); // power2.out equivalent
    let start;
    const step = (now) => {
      if (start === undefined) start = now;
      const t = Math.min((now - start) / duration, 1);
      el.textContent = Math.round(easeOut(t) * target).toLocaleString() + suffix;
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  const initCounts = (root) => {
    const counters = [...root.querySelectorAll('[data-count]')].filter((el) => !el.dataset.cv3CountInit);
    counters.forEach((el) => { el.dataset.cv3CountInit = '1'; });
    if (!counters.length) return;
    if (reduceMotion || noAnim || !hasIO) {
      counters.forEach(setFinalCount);
      return;
    }
    const cio = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { cio.unobserve(en.target); runCount(en.target); }
      });
    }, { rootMargin: '0% 0% -12% 0%', threshold: 0.01 });
    counters.forEach((el) => cio.observe(el));
  };

  /* ------------------------------------------------------------------ Entrance reveals */
  const revealTargets = (root) =>
    [...root.querySelectorAll('[data-rev], [data-card], [data-quote], [data-rev-group]')];

  const initReveals = (root) => {
    root.querySelectorAll('[data-card]').forEach((el, i) => el.style.setProperty('--d', `${(i % 4) * 70}ms`));
    root.querySelectorAll('[data-quote]').forEach((el, i) => el.style.setProperty('--d', `${(i % 3) * 70}ms`));
    root.querySelectorAll('[data-rev-group]').forEach((group) =>
      [...group.children].forEach((child, i) => child.style.setProperty('--d', `${i * 80}ms`)));

    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('cv3-is-in'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0% 0% -8% 0%', threshold: 0.01 });
    revealTargets(root).forEach((el) => io.observe(el));
  };

  /* --------------------------------------------------------------- FAQ scrollspy
     Highlights the quick-jump chip for the category currently in view. */
  const initFaqJump = () => {
    const jump = document.querySelector('.cv3-faq-jump');
    if (!jump || jump.dataset.cv3SpyInit) return;
    jump.dataset.cv3SpyInit = '1';
    const chips = [...jump.querySelectorAll('.cv3-faq-jump__chip')];
    const cats = chips.map((c) => document.querySelector(c.getAttribute('href'))).filter(Boolean);
    if (!cats.length) return;
    const setActive = (id) => chips.forEach((c) => {
      const on = c.getAttribute('href') === '#' + id;
      c.classList.toggle('cv3-is-active', on);
      if (on) c.setAttribute('aria-current', 'true'); else c.removeAttribute('aria-current');
    });
    const LINE = 172; // just below the sticky nav + chip rail
    const update = () => {
      let current = cats[0];
      for (const c of cats) { if (c.getBoundingClientRect().top - LINE <= 0) current = c; }
      setActive(current.id);
    };
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { update(); ticking = false; });
    }, { passive: true });
    update();
  };

  /* --------------------------------------------------------------- why cove: one claim open at a time */
  const initWhyRows = (root) => {
    root.querySelectorAll('details.cv3-why__row').forEach((row) => {
      if (row.dataset.cv3WhyInit) return;
      row.dataset.cv3WhyInit = '1';
      row.addEventListener('toggle', () => {
        if (!row.open) return;
        document.querySelectorAll('details.cv3-why__row[open]').forEach((other) => {
          if (other !== row) other.open = false;
        });
      });
    });
  };

  /* --------------------------------------------------------------- Country auto-detect
     Non-Plus stores get no geolocation: every fresh visitor lands in the primary
     (US) market until Shopify's localization cookie is set. Ask Shopify's own
     suggestion endpoint (IP + browser based) once per browser, apply the suggested
     country via the standard localization POST (sets the cookie server-side), and
     never fight a later manual choice. SEO bots crawl from the US -> unaffected. */
  const geoCountry = () => {
    if (window.Shopify && window.Shopify.designMode) return;
    let done;
    try { done = localStorage.getItem('cv3-geo-done'); } catch (e) { return; }
    if (done) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cur = (window.Shopify && window.Shopify.country) || '';
    fetch(root + 'browsing_context_suggestions.json?country%5Benabled%5D=true&country%5Bexclude%5D=' + encodeURIComponent(cur))
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        try { localStorage.setItem('cv3-geo-done', '1'); } catch (e) {}
        const sug = d && d.suggestions && d.suggestions[0];
        const c = sug && sug.parts && sug.parts.country;
        if (!c || !c.handle || c.handle === cur) return;
        const form = document.createElement('form');
        form.method = 'post'; form.action = root + 'localization'; form.hidden = true;
        [['form_type', 'localization'], ['utf8', '\u2713'], ['country_code', c.handle],
         ['return_to', location.pathname + location.search]].forEach(([n, v]) => {
          const i = document.createElement('input'); i.type = 'hidden'; i.name = n; i.value = v; form.appendChild(i);
        });
        document.body.appendChild(form);
        form.submit();
      })
      .catch(() => {});
  };
  geoCountry();

  /* ------------------------------------------------------------------ Boot */
  const initAll = (root) => { initMarquees(root); initCarousel(root); initPress(root); initFilm(root); initWhyRows(root); initFaqJump(); };
  initAll(document);
  initCounts(document);

  const motionOn = !reduceMotion && !noAnim && hasIO;
  if (motionOn) {
    document.documentElement.classList.add('cv3-anim'); // gates the hidden initial state (no-JS stays visible)
    initReveals(document);
  }

  // Fresh DOM arriving after first boot (theme-editor section re-render, or the
  // PDP's in-place flavour swap): re-wire behaviors and surface the content
  // immediately (no reveal replay).
  const initSwapped = (root) => {
    if (!root || !root.querySelectorAll) return;
    initAll(root);
    root.querySelectorAll('[data-count]').forEach((el) => { el.dataset.cv3CountInit = '1'; setFinalCount(el); });
    if (motionOn) revealTargets(root).forEach((el) => el.classList.add('cv3-is-in'));
  };
  window.cv3Init = initSwapped;
  document.addEventListener('shopify:section:load', (e) => initSwapped(e.target));
})();
